import React from "react";

import { faTrashAlt, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { get } from "lodash-es";
import { Controller, FieldError, useFormContext } from "react-hook-form";

import {
  Audience,
  ManifestIntegrationProvider,
  ManifestResourceDefinition,
} from "src/api/connectApi/manifestTypes";
import { Button } from "src/base-components/Button";
import { ExternalLink } from "src/base-components/ExternalLink";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import { Form } from "src/connections/config/manifest/common/Form";
import {
  ManifestFormType,
  ManifestResourceCreate,
} from "src/connections/config/manifest/types";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type ResourceConfigFormProps = {
  resource: ManifestResourceCreate;
  index: number;
  manifest: ManifestIntegrationProvider;
  onRemove: (resourceId: string) => void;
  onSelectResource: (
    currentResourceInstance: ManifestResourceCreate,
    newResource: string,
  ) => void;
  onlyOneResourceInConnection: boolean;
};

export const isManifestResourceAvailableInAWSRegion = (
  resourceManifest: ManifestResourceDefinition,
  AWSRegion: string | undefined,
): boolean => {
  if (isFeatureFlagEnabled(FEATURE_FLAGS.powertools)) {
    return true;
  }
  if (resourceManifest.geographic_restrictions) {
    return resourceManifest.geographic_restrictions.some((regionPrefix) =>
      AWSRegion?.startsWith(regionPrefix),
    );
  }
  return true;
};

/**
 * Gets the effective audience for a resource.
 * Resource-level audience takes precedence over provider-level audience.
 * If resource-level audience exists, use it; otherwise, use provider-level audience.
 * Safety guard: if provider-level audience is "internal", all resources are considered "internal".
 */
export const getResourceEffectiveAudience = (
  resource: ManifestResourceDefinition,
  providerAudience: Audience,
): Audience => {
  // Safety guard: if provider is internal, all resources are internal
  if (providerAudience === "internal") {
    return "internal";
  }
  return resource.audience ?? providerAudience;
};

/**
 * Checks if a resource should be visible based on audience filtering.
 *
 * For internal providers: All resources are always visible (no individual filtering).
 * Access control is handled at the provider level (CreateConnectionGrid filters internal
 * providers unless flags are enabled). Once you're working with an internal provider,
 * all resources should be visible to prevent crashes when editing existing connections.
 *
 * For non-internal providers: Resources with "internal" audience are hidden unless
 * the integrationVelocity feature flag is enabled.
 */
export const isManifestResourceVisibleByAudience = (
  resource: ManifestResourceDefinition,
  providerAudience: Audience,
): boolean => {
  if (providerAudience === "internal") {
    return true;
  }
  if (isFeatureFlagEnabled(FEATURE_FLAGS.integrationVelocity)) {
    return true;
  }

  const effectiveAudience = getResourceEffectiveAudience(
    resource,
    providerAudience,
  );

  return effectiveAudience !== "internal";
};

export const ResourceConfigForm: React.FC<ResourceConfigFormProps> = ({
  resource,
  index,
  manifest,
  onRemove,
  onSelectResource,
  onlyOneResourceInConnection,
}) => {
  const workspaceData = useWorkspaceAndOrg();
  const AWSRegion = workspaceData.workspace?.aws_region;
  const formMethods = useFormContext<ManifestFormType>();
  const resourceOptions = Object.entries(manifest.resources)
    .filter(([_, resource]) =>
      isManifestResourceAvailableInAWSRegion(resource, AWSRegion),
    )
    .filter(([_, resource]) =>
      isManifestResourceVisibleByAudience(resource, manifest.audience),
    )
    .map(([key, resource]) => ({
      key,
      value: resource.display_name,
      disabled: false,
    }));
  const resourceManifestDefinition = manifest.resources[resource.resource];
  const resourceInputs = resourceManifestDefinition.resource_inputs;
  // The "See source flow" link is only relevant for manifests promoted via the
  // S3 upload path (s3_upload mode), where the source flow still exists.
  const isS3UploadManifest = manifest.meta?.s3_promoted === true;

  const removeResourceDisabled = onlyOneResourceInConnection;

  const nameFieldError = get(
    formMethods.formState.errors,
    `resource_configs.${index}.name`,
  ) as FieldError | undefined;
  const nameFieldErrorMessage = nameFieldError?.message || "Invalid value";

  return (
    <div
      key={resource.id}
      className="mb-4 rounded-lg bg-gray-50 px-4 pt-4 pb-9"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <h3 className="font-inter-normal-12px text-gray-600 uppercase">
            Resource {index + 1}
          </h3>
          {isS3UploadManifest &&
            resourceManifestDefinition.nib_meta?.flow_url && (
              <Tooltip placement="top" title="See source flow">
                <ExternalLink
                  className="text-gray-500"
                  data-loc="resource-source-flow-link"
                  href={`/decide/${resourceManifestDefinition.nib_meta.flow_url}`}
                >
                  <Icon icon={faUpRightFromSquare} size="2xs" />
                </ExternalLink>
              </Tooltip>
            )}
        </div>
        <Tooltip
          disabled={!removeResourceDisabled}
          placement="top"
          title="At least one resource is required"
          asChild
        >
          <div>
            <Button
              dataLoc="resource-delete-button"
              disabled={removeResourceDisabled}
              iconLeft={faTrashAlt}
              size="sm"
              variant="secondary"
              onClick={() => onRemove(resource.id)}
            >
              Delete resource
            </Button>
          </div>
        </Tooltip>
      </div>
      <FormItem className="mt-4" label="Type">
        <Select
          dataLoc="manifest-resource-config-form-type"
          options={resourceOptions}
          placement="bottom"
          value={resource.resource}
          onChange={(newResource) => onSelectResource(resource, newResource)}
        />
      </FormItem>
      <FormItem
        description="A name that describes the resource"
        label="Resource name"
        isRequired
      >
        <Input
          data-loc="manifest-resource-config-form-name"
          errored={!!nameFieldError}
          placeholder={resourceManifestDefinition.display_name}
          suffixIcon={nameFieldError ? { icon: faWarning } : undefined}
          fullWidth
          {...formMethods.register(`resource_configs.${index}.name`, {
            required: "Resource name is required",
          })}
        />
        {nameFieldError && <ErrorHint>{nameFieldErrorMessage}</ErrorHint>}
      </FormItem>
      <Form
        formPath={`resource_configs.${index}.configuration`}
        prefix="manifest-resource-config-form"
        schema={resourceInputs}
      />
      <div className="mt-4 flex items-center justify-between">
        <div className="flex flex-col">
          <h3 className="inline-block font-inter-semibold-13px text-gray-800">
            Include raw provider responses
            <FormItem.Help body="With this enabled, the untouched API response from this resource will be returned alongside published Flows' output. The API response will only be included on Flow Runs that successfully fetch an API response, and not when using a cached response. Taktile never stores API responses." />
          </h3>
          <span className="mt-1 font-inter-normal-12px text-gray-500">
            Include the raw API response for this resource in published Flows'
            API responses. Note that this may expose sensitive data in the API
            response.
          </span>
        </div>
        <div className="ml-1">
          <Controller
            control={formMethods.control}
            name={`resource_configs.${index}.has_raw_response_enabled`}
            render={({ field: { value, onChange } }) => (
              <Switch
                enabled={value as boolean}
                onChange={() => onChange(!value)}
              />
            )}
          />
        </div>
      </div>
      {isFeatureFlagEnabled(FEATURE_FLAGS.manifestRawRequests) &&
        manifest.supports_raw_requests && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="inline-block font-inter-semibold-13px text-gray-800">
                Include raw requests made to the provider API
                <FormItem.Help body="With this enabled, the raw HTTP request to the provider will be returned with the Taktile API response alongside published Flows' output. The request will not be included when using a cached response. Taktile never stores raw requests." />
              </h3>
              <span className="mt-1 font-inter-normal-12px text-gray-500">
                Include the raw request sent to the provider in the published
                Flows' API responses. Note that this may expose sensitive data
                in the API response.
              </span>
            </div>
            <div className="ml-2">
              <Controller
                control={formMethods.control}
                name={`resource_configs.${index}.has_raw_requests_enabled_in_resource`}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    enabled={value as boolean}
                    onChange={() => onChange(!value)}
                  />
                )}
              />
            </div>
          </div>
        )}
      {isFeatureFlagEnabled(FEATURE_FLAGS.disableSensitiveDataMasking) && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="inline-block font-inter-semibold-13px text-gray-800">
              Disable sensitive data stripping
              <FormItem.Help body="Disable sensitive data stripping for this resource." />
            </h3>
            <span className="mt-1 font-inter-normal-12px text-gray-500">
              Disable sensitive data stripping for this resource. Note this may
              expose sensitive data in the API response and store it in
              Taktile's cache until expiration.
            </span>
          </div>
          <div className="ml-1">
            <Controller
              control={formMethods.control}
              name={`resource_configs.${index}.disable_sensitive_data_stripping`}
              render={({ field: { value, onChange } }) => (
                <Switch
                  enabled={value as boolean}
                  onChange={() => onChange(!value)}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};
